/**
 * Email Debug Utilities
 * Use these functions to test and debug email sending
 */

import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Check recent emails in the queue
 * @param {number} count - Number of recent emails to fetch
 * @returns {Promise<Array>} Recent email documents
 */
export async function checkEmailQueue(count = 10) {
  try {
    const mailRef = collection(db, "mail");
    const q = query(mailRef, orderBy("createdAt", "desc"), limit(count));
    const snapshot = await getDocs(q);
    
    const emails = [];
    snapshot.forEach((doc) => {
      emails.push({
        id: doc.id,
        ...doc.data(),
      });
    });
    
    console.log(`📧 Found ${emails.length} emails in queue:`);
    emails.forEach((email, i) => {
      console.log(`\n${i + 1}. Email ID: ${email.id}`);
      console.log(`   To: ${email.to}`);
      console.log(`   Subject: ${email.message?.subject}`);
      console.log(`   Status: ${email.status || 'pending'}`);
      console.log(`   Created: ${email.createdAt?.toDate?.() || 'N/A'}`);
      if (email.delivery) {
        console.log(`   Delivery: ${JSON.stringify(email.delivery)}`);
      }
      if (email.error) {
        console.log(`   ❌ Error: ${JSON.stringify(email.error)}`);
      }
    });
    
    return emails;
  } catch (error) {
    console.error("❌ Failed to check email queue:", error);
    return [];
  }
}

/**
 * Test email service by checking if mail collection exists
 * @returns {Promise<Object>} Test results
 */
export async function testEmailSetup() {
  console.log("🧪 Testing Email Setup...\n");
  
  const results = {
    mailCollectionExists: false,
    emailCount: 0,
    hasExtension: "Unknown - check Firebase Console",
    recommendation: ""
  };
  
  try {
    // Check if mail collection exists and has documents
    const mailRef = collection(db, "mail");
    const snapshot = await getDocs(query(mailRef, limit(1)));
    
    results.mailCollectionExists = true;
    
    // Count total emails
    const allEmails = await getDocs(mailRef);
    results.emailCount = allEmails.size;
    
    console.log("✅ Mail collection exists");
    console.log(`📊 Total emails in queue: ${results.emailCount}`);
    
    if (results.emailCount === 0) {
      results.recommendation = "No emails found. Try sending an invite to queue an email.";
      console.log("\n💡 " + results.recommendation);
    } else {
      // Check if any emails have been processed
      let processed = 0;
      let pending = 0;
      let errors = 0;
      
      allEmails.forEach((doc) => {
        const data = doc.data();
        if (data.delivery) processed++;
        else if (data.error) errors++;
        else pending++;
      });
      
      console.log(`   ✅ Processed: ${processed}`);
      console.log(`   ⏳ Pending: ${pending}`);
      console.log(`   ❌ Errors: ${errors}`);
      
      if (pending > 0 && processed === 0) {
        results.recommendation = "Emails are queued but not being processed. The Firebase Email Extension may not be installed.";
        console.log("\n⚠️ " + results.recommendation);
      } else if (errors > 0) {
        results.recommendation = "Some emails failed. Check SMTP configuration in Firebase Extension settings.";
        console.log("\n⚠️ " + results.recommendation);
      } else if (processed > 0) {
        results.recommendation = "Email system is working! Check spam folder if not receiving emails.";
        console.log("\n✅ " + results.recommendation);
      }
    }
    
  } catch (error) {
    results.mailCollectionExists = false;
    results.recommendation = "Mail collection doesn't exist yet. Send your first invite to create it.";
    console.error("\n❌ Error:", error.message);
    console.log("\n💡 " + results.recommendation);
  }
  
  console.log("\n" + "=".repeat(50));
  console.log("🔧 Next Steps:");
  console.log("1. Go to Firebase Console → Extensions");
  console.log("2. Install 'Trigger Email from Firestore' if not installed");
  console.log("3. Configure SMTP settings");
  console.log("4. Send a test invite from Prospect Tracker");
  console.log("5. Run checkEmailQueue() to verify");
  console.log("=".repeat(50) + "\n");
  
  return results;
}

// Make functions available globally in browser console
if (typeof window !== 'undefined') {
  window.checkEmailQueue = checkEmailQueue;
  window.testEmailSetup = testEmailSetup;
  console.log("📧 Email debug functions loaded!");
  console.log("   → Run testEmailSetup() to check configuration");
  console.log("   → Run checkEmailQueue() to see recent emails");
}

